import { readProjectSettings } from './adminSettings';
import { readAdminSession } from './authProvider';

type AdminApiEnvelope<T> = {
    success?: boolean;
    data?: T;
    statusCode?: number;
    errorMessage?: string | null;
    message?: string;
    error?: string;
    title?: string;
    detail?: string;
};

type AdminApiListEnvelope<T> = {
    items?: T[];
    records?: T[];
    results?: T[];
    total?: number;
    totalCount?: number;
    count?: number;
};

export type PendingUserPhotoRecord = {
    id: string;
    userId: string;
    displayName: string;
    email: string;
    status: string;
    pendingPhotoUrl: string;
    thumbnailUrl: string;
    createdAt: string;
    updatedAt: string;
    raw: Record<string, unknown>;
};

export type GetPendingUserPhotoListParams = {
    page: number;
    pageSize: number;
    search?: string;
};

function trimTrailingSlash(value: string): string {
    return value.replace(/\/+$/, '');
}

function trimSlashes(value: string): string {
    return value.replace(/^\/+|\/+$/g, '');
}

function hasVersionedApiPath(value: string): boolean {
    return /\/api\/v[^/]+$/i.test(trimTrailingSlash(value));
}

function getAdminApiConfig() {
    const settings = readProjectSettings();
    const session = readAdminSession();
    const baseUrl = trimTrailingSlash(
        String(
            import.meta.env.VITE_API_BASE_URL ??
                import.meta.env.VITE_API_URL ??
                settings.adminApiBaseUrl ??
                'https://test-api.pteultra.com/api/v1',
        ),
    );
    const apiVersion = String(import.meta.env.VITE_API_VERSION ?? settings.adminApiVersion ?? '1');
    const adminToken = String(
        session?.adminToken ?? session?.accessToken ?? import.meta.env.VITE_ADMIN_TOKEN ?? settings.adminApiToken ?? '',
    );

    return { baseUrl, apiVersion, adminToken };
}

function getAdminAssetBaseUrl(): string {
    const fileBaseUrl = trimTrailingSlash(String(import.meta.env.VITE_BASE_FILE_URL ?? ''));
    if (fileBaseUrl) {
        return fileBaseUrl;
    }

    const { baseUrl } = getAdminApiConfig();
    if (hasVersionedApiPath(baseUrl)) {
        return trimTrailingSlash(baseUrl.replace(/\/api\/v[^/]+$/i, ''));
    }

    return trimTrailingSlash(baseUrl);
}

function resolveAdminAssetUrl(value: unknown): string {
    const rawValue = String(value ?? '').trim();
    if (!rawValue) return '';

    if (/^https?:\/\//i.test(rawValue)) {
        return rawValue;
    }

    return new URL(rawValue.replace(/^\/+/, ''), `${getAdminAssetBaseUrl()}/`).toString();
}

function getErrorMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct =
            candidate.errorMessage ?? candidate.message ?? candidate.error ?? candidate.title ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function getSuccessMessage(payload: unknown, fallback: string): string {
    if (typeof payload === 'string' && payload.trim()) return payload;
    if (payload && typeof payload === 'object') {
        const candidate = payload as Record<string, unknown>;
        const direct = candidate.message ?? candidate.detail;
        if (typeof direct === 'string' && direct.trim()) return direct;
    }
    return fallback;
}

function unwrapAdminApiPayload<T>(payload: T | AdminApiEnvelope<T>): T {
    if (!payload || typeof payload !== 'object' || !('data' in payload)) {
        return payload as T;
    }

    const envelope = payload as AdminApiEnvelope<T>;

    if (envelope.success === false) {
        throw new Error(getErrorMessage(envelope, 'Admin user photo request failed'));
    }

    if (envelope.data !== undefined) {
        return envelope.data;
    }

    return payload as T;
}

function asObject(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }

    return {};
}

function getStringValue(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
}

function getFirstString(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const value = getStringValue(source[key]);
        if (value) {
            return value;
        }
    }

    return '';
}

function getFirstUrl(source: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
        const rawValue = source[key];
        if (typeof rawValue === 'string' && rawValue.trim()) {
            return resolveAdminAssetUrl(rawValue);
        }

        const nested = asObject(rawValue);
        const nestedValue = getFirstString(nested, ['url', 'uri', 'src', 'path', 'fileUrl', 'imageUrl', 'photoUrl']);
        if (nestedValue) {
            return resolveAdminAssetUrl(nestedValue);
        }
    }

    return '';
}

function getDisplayName(record: Record<string, unknown>, user: Record<string, unknown>): string {
    const directDisplayName =
        getFirstString(record, ['displayName', 'fullName', 'name', 'userName']) ||
        getFirstString(user, ['displayName', 'fullName', 'name', 'userName']);

    if (directDisplayName) {
        return directDisplayName;
    }

    const firstName = getFirstString(record, ['firstName']) || getFirstString(user, ['firstName']);
    const lastName = getFirstString(record, ['lastName']) || getFirstString(user, ['lastName']);

    return [firstName, lastName].filter(Boolean).join(' ');
}

function getListPayloadObject(payload: unknown): Record<string, unknown> | null {
    const unwrapped = unwrapAdminApiPayload(
        payload as Record<string, unknown> | AdminApiEnvelope<Record<string, unknown>>,
    );

    if (unwrapped && typeof unwrapped === 'object' && !Array.isArray(unwrapped)) {
        return unwrapped as Record<string, unknown>;
    }

    return null;
}

function getListRecords<T>(payload: unknown): T[] {
    const unwrapped = unwrapAdminApiPayload(payload as T[] | AdminApiEnvelope<T[]>);

    if (Array.isArray(unwrapped)) {
        return unwrapped;
    }

    if (unwrapped && typeof unwrapped === 'object') {
        const candidate = unwrapped as AdminApiListEnvelope<T> & Record<string, unknown>;
        if (Array.isArray(candidate.items)) {
            return candidate.items;
        }
        if (Array.isArray(candidate.records)) {
            return candidate.records;
        }
        if (Array.isArray(candidate.results)) {
            return candidate.results;
        }
        if (Array.isArray(candidate.data)) {
            return candidate.data as T[];
        }
    }

    return [];
}

function getListTotal(payload: unknown, fallback: number): number {
    const unwrapped = getListPayloadObject(payload);

    if (unwrapped) {
        if (typeof unwrapped.total === 'number') return unwrapped.total;
        if (typeof unwrapped.totalCount === 'number') return unwrapped.totalCount;
        if (typeof unwrapped.count === 'number') return unwrapped.count;
    }

    return fallback;
}

function normalizePendingUserPhotoRecord(item: unknown, fallbackId: string): PendingUserPhotoRecord {
    const record = asObject(item);
    const user = asObject(record.user ?? record.appUser ?? record.profile);
    const photo = asObject(record.photo ?? record.userPhoto ?? record.profilePhoto);

    const resolvedUserId =
        getFirstString(record, ['userId', 'appUserId', 'applicationUserId', 'id']) ||
        getFirstString(user, ['userId', 'appUserId', 'applicationUserId', 'id']) ||
        fallbackId;

    const pendingPhotoUrl =
        getFirstUrl(record, [
            'profileImageUrlPending',
            'ProfileImageUrlPending',
            'profile_image_url_pending',
            'pendingProfileImageUrl',
            'pendingPhotoUrl',
            'pendingProfilePhotoUrl',
            'photoUrl',
            'PhotoUrl',
            'profilePhotoUrl',
            'ProfilePhotoUrl',
            'imageUrl',
            'ImageUrl',
            'avatarUrl',
            'fileUrl',
            'mediaUrl',
        ]) ||
        getFirstUrl(photo, [
            'profileImageUrlPending',
            'ProfileImageUrlPending',
            'profile_image_url_pending',
            'pendingProfileImageUrl',
            'pendingPhotoUrl',
            'pendingProfilePhotoUrl',
            'photoUrl',
            'PhotoUrl',
            'profilePhotoUrl',
            'ProfilePhotoUrl',
            'imageUrl',
            'ImageUrl',
            'avatarUrl',
            'fileUrl',
            'mediaUrl',
        ]);

    const thumbnailUrl =
        getFirstUrl(record, [
            'pendingThumbnailUrl',
            'PendingThumbnailUrl',
            'pending_thumbnail_url',
            'thumbnailUrl',
            'ThumbnailUrl',
            'thumbnail_url',
            'photoThumbnailUrl',
            'profileThumbnailUrl',
        ]) ||
        getFirstUrl(photo, [
            'pendingThumbnailUrl',
            'PendingThumbnailUrl',
            'pending_thumbnail_url',
            'thumbnailUrl',
            'ThumbnailUrl',
            'thumbnail_url',
            'photoThumbnailUrl',
            'profileThumbnailUrl',
        ]) ||
        pendingPhotoUrl;

    return {
        id: resolvedUserId,
        userId: resolvedUserId,
        displayName: getDisplayName(record, user),
        email:
            getFirstString(record, ['email', 'emailAddress']) ||
            getFirstString(user, ['email', 'emailAddress']),
        status:
            getFirstString(record, ['status', 'moderationStatus']) ||
            getFirstString(photo, ['status', 'moderationStatus']) ||
            'Pending',
        pendingPhotoUrl,
        thumbnailUrl,
        createdAt:
            getFirstString(record, ['createdAt', 'submittedAt', 'uploadedAt', 'requestedAt']) ||
            getFirstString(photo, ['createdAt', 'submittedAt', 'uploadedAt', 'requestedAt']),
        updatedAt:
            getFirstString(record, ['updatedAt', 'modifiedAt']) ||
            getFirstString(photo, ['updatedAt', 'modifiedAt']),
        raw: record,
    };
}

function toQueryValue(value: unknown): string {
    if (value === undefined || value === null) return '';
    return String(value).trim();
}

async function requestAdminUserPhoto<T>(
    path = '',
    init: RequestInit = {},
    query?: Record<string, string>,
): Promise<T> {
    const { baseUrl, apiVersion, adminToken } = getAdminApiConfig();
    const apiRoot = hasVersionedApiPath(baseUrl) ? baseUrl : `${baseUrl}/api/v${apiVersion}`;
    const requestPath = trimSlashes(path);
    const url = new URL(
        requestPath ? `${apiRoot}/AdminUserPhoto/${requestPath}` : `${apiRoot}/AdminUserPhoto`,
    );

    for (const [key, value] of Object.entries(query ?? {})) {
        if (value !== '') {
            url.searchParams.set(key, value);
        }
    }

    const headers = new Headers(init.headers);
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }
    if (init.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }
    if (adminToken && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${adminToken}`);
    }

    const response = await fetch(url.toString(), {
        ...init,
        headers,
    });

    if (response.status === 204) {
        return undefined as T;
    }

    const contentType = response.headers.get('content-type') ?? '';
    const payload = contentType.includes('application/json') ? await response.json() : await response.text();

    if (!response.ok) {
        throw new Error(getErrorMessage(payload, `${response.status} ${response.statusText}`));
    }

    if (payload && typeof payload === 'object' && 'success' in (payload as Record<string, unknown>)) {
        const envelope = payload as AdminApiEnvelope<unknown>;
        if (envelope.success === false) {
            throw new Error(getErrorMessage(envelope, 'Admin user photo request failed'));
        }
    }

    return payload as T;
}

async function runPendingUserPhotoAction(action: 'approve' | 'reject', userId: string) {
    const normalizedUserId = String(userId).trim();

    if (!normalizedUserId) {
        throw new Error('User ID is required');
    }

    const payload = await requestAdminUserPhoto<unknown | AdminApiEnvelope<unknown>>(
        `${action}/${encodeURIComponent(normalizedUserId)}`,
        { method: 'POST' },
    );

    return {
        data: unwrapAdminApiPayload(payload),
        message: getSuccessMessage(
            payload,
            action === 'approve'
                ? 'Pending profile photo approved successfully.'
                : 'Pending profile photo rejected successfully.',
        ),
    };
}

export async function getPendingUserPhotos(params: GetPendingUserPhotoListParams) {
    const query = {
        page: String(Math.max(1, params.page)),
        pageSize: String(Math.max(1, params.pageSize)),
        search: toQueryValue(params.search ?? ''),
    };

    const payload = await requestAdminUserPhoto<unknown | AdminApiEnvelope<unknown>>(
        'pending',
        { method: 'GET' },
        query,
    );
    const records = getListRecords<unknown>(payload).map((item, index) =>
        normalizePendingUserPhotoRecord(item, `pending-photo-${query.page}-${index}`),
    );

    return {
        data: records,
        total: getListTotal(payload, records.length),
    };
}

export async function approvePendingUserPhoto(userId: string) {
    return runPendingUserPhotoAction('approve', userId);
}

export async function rejectPendingUserPhoto(userId: string) {
    return runPendingUserPhotoAction('reject', userId);
}